import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Row,
  Col,
  Form,
  Button,
  Table,
  Dropdown,
  Badge,
  Alert,
  ButtonGroup,
} from "react-bootstrap";
import { gymMembersAPI } from "../../api/gymMembers";
import { subscriptionAPI } from "../../api/subscription";

import {
  FaFilter,
  FaDownload,
  FaPrint,
  FaFileExcel,
  FaExclamationTriangle,
  FaSyncAlt,
} from "react-icons/fa";

import * as XLSX from "xlsx";
import "./MemberReport.css";
import "react-datepicker/dist/react-datepicker.css";

const ExpiryReport = () => {
  const [members, setMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [subscriptionTypes, setSubscriptionTypes] = useState([]);
  const [subscriptionPeriods, setSubscriptionPeriods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingSubscriptionData, setLoadingSubscriptionData] = useState(false);
  const [error, setError] = useState(null);

  // State for filters
  const [selectedBatch, setSelectedBatch] = useState("all");
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const [stats, setStats] = useState({
    totalMembers: 0,
    activeMembers: 0,
    newThisMonth: 0,
    inactiveMembers: 0,
    totalRevenue: 0,
    pendingRevenue: 0,
  });
  const [batches, setBatches] = useState([
    "all",
    "morning",
    "early_morning",
    "evening",
  ]);
  const [showExpired, setShowExpired] = useState(false);
  const [planSource, setPlanSource] = useState("both"); // Default to 'both' // 'current', 'previous', or 'both'
  const tableRef = useRef();

  const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884D8"];

  // Months array for dropdown
  const months = [
    { id: 1, name: "January" },
    { id: 2, name: "February" },
    { id: 3, name: "March" },
    { id: 4, name: "April" },
    { id: 5, name: "May" },
    { id: 6, name: "June" },
    { id: 7, name: "July" },
    { id: 8, name: "August" },
    { id: 9, name: "September" },
    { id: 10, name: "October" },
    { id: 11, name: "November" },
    { id: 12, name: "December" },
  ];

  // Generate years (current year and 5 previous years)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 6 }, (_, i) => currentYear - i + 1); // +1 to include next year

  useEffect(() => {
    fetchSubscriptionData();
    fetchMembers();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [
    selectedBatch,
    selectedMonth,
    selectedYear,
    showExpired,
    planSource,
    members,
  ]);

  const fetchSubscriptionData = async () => {
    setLoadingSubscriptionData(true);
    setError(null);
    try {
      // Fetch subscription types using the API
      const typesResponse = await subscriptionAPI.getSubscriptionTypes();
      setSubscriptionTypes(typesResponse.data);

      // Fetch subscription periods using the API
      const periodsResponse = await subscriptionAPI.getSubscriptionPeriods();
      setSubscriptionPeriods(periodsResponse.data);
    } catch (error) {
      console.error("Error fetching subscription data:", error);
      setError(
        "Failed to load subscription types and periods. Some filters may not work properly."
      );

      // Fallback data in case API fails
      setSubscriptionTypes([
        { id: 1, name: "Gym" },
        { id: 2, name: "Cross fit" },
        { id: 3, name: "Cross fit + Gym" },
        { id: 4, name: "Personal Training" },
      ]);

      setSubscriptionPeriods([
        { id: 1, months: 1 },
        { id: 2, months: 2 },
        { id: 3, months: 3 },
        { id: 4, months: 4 },
        { id: 5, months: 5 },
        { id: 6, months: 6 },
      ]);
    } finally {
      setLoadingSubscriptionData(false);
    }
  };

  const fetchMembers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await gymMembersAPI.getMembers();
      console.log("API Response:", response); // Debug log

      // Check different possible response structures
      let membersData = [];

      if (response.data && response.data.data) {
        // Structure: { data: { data: [...] } }
        membersData = response.data.data;
      } else if (response.data && Array.isArray(response.data)) {
        // Structure: { data: [...] }
        membersData = response.data;
      } else if (response.data && response.data.results) {
        // Structure: { data: { results: [...] } }
        membersData = response.data.results;
      } else if (response.data) {
        // Structure: { data: { ... } } - try to extract data
        membersData = response.data;
      } else {
        membersData = [];
      }

      console.log("Extracted members data:", membersData); // Debug log

      // Make sure it's an array
      if (!Array.isArray(membersData)) {
        membersData = [];
      }

      setMembers(membersData);
      calculateStats(membersData);
    } catch (error) {
      console.error("Error fetching members:", error);
      console.error("Error details:", error.response);
      setError(
        "Failed to load member data. Please try again. Error: " + error.message
      );
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    if (!members.length) return;

    let filtered = [...members];
    let processedMembers = [];

    // Process each member based on plan source selection
    filtered.forEach((member) => {
      const plans = [];

      if (planSource === "current" || planSource === "both") {
        if (member.current_plan) {
          plans.push({
            ...member.current_plan,
            source: "current",
            memberInfo: member,
          });
        }
      }

      if (planSource === "previous" || planSource === "both") {
        if (member.previous_plan) {
          plans.push({
            ...member.previous_plan,
            source: "previous",
            memberInfo: member,
          });
        }
      }

      // Add member even if no plans (for completeness)
      if (plans.length === 0) {
        processedMembers.push({
          ...member,
          plan: null,
          planSource: "none",
        });
      } else {
        // Create separate entries for each plan
        plans.forEach((plan) => {
          processedMembers.push({
            ...member,
            plan: plan,
            planSource: plan.source,
          });
        });
      }
    });

    // Now filter the processed members
    let result = processedMembers;

    // Filter by batch
    if (selectedBatch !== "all") {
      result = result.filter((item) => {
        if (!item.plan) return false;
        return item.plan.batch === selectedBatch;
      });
    }

    // Filter by month and year (expiry date)
    result = result.filter((item) => {
      if (!item.plan || !item.plan.expiry_date) return false;

      const expiryDate = new Date(item.plan.expiry_date);
      const expiryMonth = expiryDate.getMonth() + 1;
      const expiryYear = expiryDate.getFullYear();

      return expiryMonth === selectedMonth && expiryYear === selectedYear;
    });

    // Filter by expired status
    if (showExpired) {
      const today = new Date();
      result = result.filter((item) => {
        if (!item.plan || !item.plan.expiry_date) return false;
        return new Date(item.plan.expiry_date) < today;
      });
    }

    setFilteredMembers(result);
  };

  const calculateStats = (data) => {
    const totalMembers = data.length;
    const activeMembers = data.filter((m) => m.is_active).length;

    // New members in last 30 days
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const newThisMonth = data.filter((m) => {
      if (!m.created_at) return false;
      const joinDate = new Date(m.created_at);
      return joinDate >= thirtyDaysAgo;
    }).length;

    // Calculate revenue from current plans only
    let totalRevenue = 0;
    let pendingRevenue = 0;

    data.forEach((member) => {
      if (member.current_plan) {
        totalRevenue += parseFloat(member.current_plan.total_amount || 0);
        pendingRevenue += parseFloat(member.current_plan.pending_amount || 0);
      }
    });

    setStats({
      totalMembers,
      activeMembers,
      newThisMonth,
      inactiveMembers: totalMembers - activeMembers,
      totalRevenue,
      pendingRevenue,
    });
  };

  const getSubscriptionTypeName = (typeId) => {
    const type = subscriptionTypes.find((t) => t.id === typeId);
    return type ? type.name : `Type ${typeId}`;
  };

  const getBatchDisplay = (batch) => {
    switch (batch) {
      case "morning":
        return "Morning";
      case "early_morning":
        return "Early Morning";
      case "evening":
        return "Evening";
      default:
        return batch;
    }
  };

  const getDaysLeft = (expiryDate) => {
    if (!expiryDate) return null;
    const expiry = new Date(expiryDate);
    const today = new Date();
    const timeDiff = expiry.getTime() - today.getTime();
    return Math.ceil(timeDiff / (1000 * 3600 * 24));
  };

  const getExpiryStatus = (expiryDate) => {
    if (!expiryDate) return "no-plan";
    const daysLeft = getDaysLeft(expiryDate);
    if (daysLeft < 0) return "expired";
    if (daysLeft <= 7) return "expiring-soon";
    return "active";
  };

  const exportToExcel = () => {
    if (filteredMembers.length === 0) {
      alert("No data to export. Please apply filters first.");
      return;
    }

    // Prepare data for Excel export
    const excelData = filteredMembers.map((member) => {
      const plan = member.plan;

      return {
        "Member ID": member.member_id,
        "Full Name": `${member.first_name} ${member.last_name}`,
        "Mobile Number": member.mobile_number,
        Email: member.email,
        "Plan Source":
          member.planSource === "current"
            ? "Current Plan"
            : member.planSource === "previous"
            ? "Previous Plan"
            : "No Plan",
        "Subscription Type": plan
          ? getSubscriptionTypeName(plan.subscription_type)
          : "N/A",
        "Subscription Period": plan
          ? `${plan.subscription_period} Month(s)`
          : "N/A",
        Batch: plan ? getBatchDisplay(plan.batch) : "N/A",
        "Plan Start Date": plan?.plan_start_date || "N/A",
        "Plan Expiry Date": plan?.expiry_date || "N/A",
        "Plan Status": plan?.fee_status || "N/A",
        "Registration Fee": plan ? `₹${plan.registration_fee}` : "N/A",
        "Plan Amount": plan ? `₹${plan.amount}` : "N/A",
        "Total Amount": plan ? `₹${plan.total_amount}` : "N/A",
        "Paid Amount": plan ? `₹${plan.paid_amount}` : "N/A",
        "Pending Amount": plan ? `₹${plan.pending_amount}` : "N/A",
        "Days Left": plan ? getDaysLeft(plan.expiry_date) : "N/A",
        "Expiry Status": plan ? getExpiryStatus(plan.expiry_date) : "N/A",
        "Member Active": member.is_active ? "Yes" : "No",
        "Join Date": member.created_at
          ? new Date(member.created_at).toLocaleDateString()
          : "N/A",
        Address: member.address,
        "Medical History": member.medical_history,
      };
    });

    // Add summary row
    const summaryRow = {
      "Member ID": "SUMMARY",
      "Full Name": "",
      "Mobile Number": "",
      Email: "",
      "Plan Source": `Total Plans: ${filteredMembers.length}`,
      "Subscription Type": `Current: ${
        filteredMembers.filter((m) => m.planSource === "current").length
      }`,
      "Subscription Period": `Previous: ${
        filteredMembers.filter((m) => m.planSource === "previous").length
      }`,
      Batch: "",
      "Plan Start Date": "",
      "Plan Expiry Date": "",
      "Plan Status": `Expired: ${
        filteredMembers.filter(
          (m) => m.plan && getExpiryStatus(m.plan.expiry_date) === "expired"
        ).length
      }`,
      "Registration Fee": "",
      "Plan Amount": "",
      "Total Amount": "",
      "Paid Amount": "",
      "Pending Amount": `Total Due: ₹${filteredMembers.reduce(
        (sum, member) => sum + parseFloat(member.plan?.pending_amount || 0),
        0
      )}`,
      "Days Left": "",
      "Expiry Status": "",
      "Member Active": "",
      "Join Date": "",
      Address: "",
      "Medical History": "",
    };

    // Create worksheet
    const ws = XLSX.utils.json_to_sheet([...excelData, {}, summaryRow]);

    // Set column widths
    const wscols = [
      { wch: 10 }, // Member ID
      { wch: 20 }, // Full Name
      { wch: 15 }, // Mobile Number
      { wch: 25 }, // Email
      { wch: 15 }, // Plan Source
      { wch: 20 }, // Subscription Type
      { wch: 18 }, // Subscription Period
      { wch: 15 }, // Batch
      { wch: 12 }, // Plan Start Date
      { wch: 12 }, // Plan Expiry Date
      { wch: 12 }, // Plan Status
      { wch: 15 }, // Registration Fee
      { wch: 12 }, // Plan Amount
      { wch: 12 }, // Total Amount
      { wch: 12 }, // Paid Amount
      { wch: 15 }, // Pending Amount
      { wch: 10 }, // Days Left
      { wch: 12 }, // Expiry Status
      { wch: 12 }, // Member Active
      { wch: 12 }, // Join Date
      { wch: 30 }, // Address
      { wch: 20 }, // Medical History
    ];
    ws["!cols"] = wscols;

    // Create workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Member Report");

    // Generate Excel file
    const fileName = `member-report-${
      new Date().toISOString().split("T")[0]
    }.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const resetFilters = () => {
    setSelectedBatch("all");
    setSelectedMonth(new Date().getMonth() + 1);
    setSelectedYear(new Date().getFullYear());
    setShowExpired(false);
    setPlanSource("current");
  };

  const getExpiryStatusBadge = (expiryDate) => {
    const status = getExpiryStatus(expiryDate);
    switch (status) {
      case "expired":
        return <Badge bg="danger">Expired</Badge>;
      case "expiring-soon":
        return <Badge bg="warning">Expiring Soon</Badge>;
      case "active":
        return <Badge bg="success">Active</Badge>;
      default:
        return <Badge bg="secondary">No Plan</Badge>;
    }
  };

  return (
    <div className="member-report-page">
      {/* Error Alert */}
      {error && (
        <Alert variant="warning" className="mb-3 d-flex align-items-center">
          <FaExclamationTriangle className="me-2" />
          <div>
            <strong>Warning:</strong> {error}
          </div>
        </Alert>
      )}

      {/* Batch Tabs Filter */}
      <Card className="mb-4 shadow-sm">
        <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
          <span>
            <FaFilter className="me-2" /> Quick Filters
          </span>
          <div>
            <Button
              variant="light"
              size="sm"
              onClick={fetchMembers}
              disabled={loading}
              className="me-2"
            >
              <FaSyncAlt className={loading ? "spinner" : ""} /> Refresh
            </Button>
            <Dropdown>
              <Dropdown.Toggle variant="success" size="sm">
                <FaDownload className="me-1" /> Export
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={exportToExcel}>
                  <FaFileExcel className="me-2" /> Excel
                </Dropdown.Item>
                <Dropdown.Item onClick={() => window.print()}>
                  <FaPrint className="me-2" /> Print
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </Card.Header>
        <Card.Body>
          <Row className="align-items-center">
            <Col md={8}>
              <div className="mb-3">
                <h6 className="mb-2">Filter by Batch</h6>
                <ButtonGroup className="w-100">
                  {batches.map((batch) => (
                    <Button
                      key={batch}
                      variant={
                        selectedBatch === batch ? "primary" : "outline-primary"
                      }
                      onClick={() => setSelectedBatch(batch)}
                      className="text-capitalize"
                    >
                      {batch === "all" ? "All Batches" : getBatchDisplay(batch)}
                    </Button>
                  ))}
                </ButtonGroup>
              </div>
            </Col>
            <Col md={4}>
              <div className="text-end">
                <Button
                  variant="outline-secondary"
                  onClick={resetFilters}
                  className="me-2"
                >
                  Reset Filters
                </Button>
              </div>
            </Col>
          </Row>

          <Row className="mt-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label>Plan Source</Form.Label>
                <Form.Select
                  value={planSource}
                  onChange={(e) => setPlanSource(e.target.value)}
                >
                  <option value="both">Both Current & Previous</option>
                  <option value="current">Current Plans Only</option>
                  <option value="previous">Previous Plans Only</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Expiry Month</Form.Label>
                <Form.Select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                >
                  {months.map((month) => (
                    <option key={month.id} value={month.id}>
                      {month.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Expiry Year</Form.Label>
                <Form.Select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                >
                  {years.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3} className="d-flex align-items-end">
              <Form.Check
                type="switch"
                id="show-expired-switch"
                label="Show Expired Only"
                checked={showExpired}
                onChange={(e) => setShowExpired(e.target.checked)}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Detailed Report Table */}
      <Card className="shadow-sm" ref={tableRef}>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <div>
            <h5 className="mb-0">Member Details</h5>
            <small className="text-muted">
              Showing {filteredMembers.length} plans for{" "}
              {months.find((m) => m.id === selectedMonth)?.name} {selectedYear}{" "}
              - Batch:{" "}
              {selectedBatch === "all" ? "All" : getBatchDisplay(selectedBatch)}
              {showExpired && " (Expired Only)"}
            </small>
          </div>
          <div>
            <Badge bg="info" className="me-2">
              Plans: {filteredMembers.length}
            </Badge>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={exportToExcel}
              disabled={filteredMembers.length === 0}
            >
              <FaFileExcel className="me-1" /> Export
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          <div className="table-responsive">
            <Table hover striped bordered>
              <thead className="table-dark">
                <tr>
                  <th>Member ID</th>
                  <th>Name</th>
                  <th>Contact</th>
                  <th>Plan Source</th>
                  <th>Batch</th>
                  <th>Plan Type</th>
                  <th>Start Date</th>
                  <th>Expiry Date</th>
                  <th>Expiry Status</th>
                  <th>Days Left</th>
                  <th>Payment Status</th>
                  <th>Amount Due</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="12" className="text-center py-5">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-2">Loading members...</p>
                    </td>
                  </tr>
                ) : filteredMembers.length === 0 ? (
                  <tr>
                    <td colSpan="12" className="text-center py-4 text-muted">
                      No plans found with the selected filters. Try changing the
                      filters.
                    </td>
                  </tr>
                ) : (
                  filteredMembers.map((member, index) => {
                    const plan = member.plan;
                    if (!plan) return null;

                    const daysLeft = getDaysLeft(plan.expiry_date);
                    const expiryStatus = getExpiryStatus(plan.expiry_date);

                    return (
                      <tr key={`${member.id}-${plan.id}-${index}`}>
                        <td>
                          <Badge bg="primary">{member.member_id}</Badge>
                        </td>
                        <td>
                          <strong>
                            {member.first_name} {member.last_name}
                          </strong>
                        </td>
                        <td>{member.mobile_number}</td>
                        <td>
                          <Badge
                            bg={
                              member.planSource === "current"
                                ? "success"
                                : "warning"
                            }
                          >
                            {member.planSource === "current"
                              ? "Current"
                              : "Previous"}{" "}
                            Plan
                          </Badge>
                        </td>
                        <td>
                          <Badge bg="info">{getBatchDisplay(plan.batch)}</Badge>
                        </td>
                        <td>
                          <Badge bg="secondary">
                            {getSubscriptionTypeName(plan.subscription_type)}
                          </Badge>
                          <div className="small text-muted">
                            {plan.subscription_period} month(s)
                          </div>
                        </td>
                        <td>
                          {plan.plan_start_date
                            ? new Date(
                                plan.plan_start_date
                              ).toLocaleDateString()
                            : "N/A"}
                        </td>
                        <td>
                          {plan.expiry_date
                            ? new Date(plan.expiry_date).toLocaleDateString()
                            : "N/A"}
                        </td>
                        <td>{getExpiryStatusBadge(plan.expiry_date)}</td>
                        <td>
                          {expiryStatus === "expired" ? (
                            <Badge bg="secondary">Expired</Badge>
                          ) : (
                            <Badge bg={daysLeft <= 7 ? "warning" : "success"}>
                              {daysLeft} days
                            </Badge>
                          )}
                        </td>
                        <td>
                          <Badge
                            bg={
                              plan.fee_status === "Paid" ? "success" : "danger"
                            }
                          >
                            {plan.fee_status}
                          </Badge>
                        </td>
                        <td className="text-danger">
                          <strong>₹{plan.pending_amount}</strong>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>

          {/* Summary Section */}
          {filteredMembers.length > 0 && (
            <div className="mt-4 p-3 bg-light rounded">
              <h6>Filter Summary</h6>
              <Row>
                <Col md={3} className="border-end">
                  <div className="text-center">
                    <div className="h4 text-primary">
                      {filteredMembers.length}
                    </div>
                    <div className="text-muted">Total Plans</div>
                  </div>
                </Col>
                <Col md={3} className="border-end">
                  <div className="text-center">
                    <div className="h4 text-warning">
                      {
                        filteredMembers.filter(
                          (m) => m.planSource === "current"
                        ).length
                      }
                    </div>
                    <div className="text-muted">Current Plans</div>
                  </div>
                </Col>
                <Col md={3} className="border-end">
                  <div className="text-center">
                    <div className="h4 text-info">
                      {
                        filteredMembers.filter(
                          (m) => m.planSource === "previous"
                        ).length
                      }
                    </div>
                    <div className="text-muted">Previous Plans</div>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="text-center">
                    <div className="h4 text-danger">
                      ₹
                      {filteredMembers
                        .reduce(
                          (sum, member) =>
                            sum + parseFloat(member.plan?.pending_amount || 0),
                          0
                        )
                        .toFixed(2)}
                    </div>
                    <div className="text-muted">Total Due</div>
                  </div>
                </Col>
              </Row>
              <div className="mt-3 text-muted small">
                Report generated on: {new Date().toLocaleString()}
                <br />
                Filters:{" "}
                {selectedBatch === "all"
                  ? "All Batches"
                  : getBatchDisplay(selectedBatch)}{" "}
                | Expiry: {months.find((m) => m.id === selectedMonth)?.name}{" "}
                {selectedYear} | Plan Source:{" "}
                {planSource === "current"
                  ? "Current Only"
                  : planSource === "previous"
                  ? "Previous Only"
                  : "Both"}{" "}
                | {showExpired ? " Expired Only" : " All Status"}
              </div>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Batch Distribution */}
      {filteredMembers.length > 0 && (
        <Card className="mt-4 shadow-sm">
          <Card.Header>
            <h5 className="mb-0">Plan Distribution</h5>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={4}>
                <Card className="mb-3">
                  <Card.Body className="text-center">
                    <h6>Current vs Previous</h6>
                    <div className="d-flex justify-content-around">
                      <div>
                        <div className="display-4 text-success">
                          {
                            filteredMembers.filter(
                              (m) => m.planSource === "current"
                            ).length
                          }
                        </div>
                        <small>Current</small>
                      </div>
                      <div>
                        <div className="display-4 text-warning">
                          {
                            filteredMembers.filter(
                              (m) => m.planSource === "previous"
                            ).length
                          }
                        </div>
                        <small>Previous</small>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={8}>
                <Row>
                  {["morning", "early_morning", "evening"].map((batch) => {
                    const count = filteredMembers.filter(
                      (member) => member.plan?.batch === batch
                    ).length;

                    if (count === 0) return null;

                    return (
                      <Col md={4} key={batch}>
                        <Card className="mb-3">
                          <Card.Body className="text-center">
                            <h6>{getBatchDisplay(batch)}</h6>
                            <div className="display-4 text-primary">
                              {count}
                            </div>
                            <div className="small text-muted">
                              {((count / filteredMembers.length) * 100).toFixed(
                                1
                              )}
                              % of total
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                    );
                  })}
                </Row>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default ExpiryReport;
